import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { usePageTitle } from '@/hooks/usePageTitle';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Plus, PackageCheck, Search, Clock, CheckCircle, CircleDot } from 'lucide-react';
import { toast } from 'sonner';
import { trNorm } from '@/lib/arama';

// Mal kabul listesi. Durum SAKLANMAZ, kalemlerden türetilir (sunucuda
// `_durum_hesapla`): acik = hiç faturalanmadı, kismi = bir kısmı faturalandı,
// kapali = tamamı faturalandı. "Faturası bekleyen" takibi buradan yapılıyor —
// süre sınırı koymuyoruz ama yaş görünüyor.
const DURUM = {
  acik: { etiket: 'Faturası bekleniyor', renk: 'bg-[#7a6a2e]/10 text-[#7a6a2e]', Ikon: Clock },
  kismi: { etiket: 'Kısmen faturalandı', renk: 'bg-[#8a6320]/10 text-[#8a6320]', Ikon: CircleDot },
  kapali: { etiket: 'Faturalandı', renk: 'bg-[#3d6e4e]/10 text-[#3d6e4e]', Ikon: CheckCircle },
};
const TUR = { irsaliye: 'İrsaliye', veresiye_fisi: 'Veresiye Fişi', siparis_fisi: 'Sipariş Fişi' };

const yasGun = (tarih) => {
  if (!tarih) return null;
  const t = new Date(tarih);
  if (Number.isNaN(t.getTime())) return null;
  return Math.floor((Date.now() - t.getTime()) / 86400000);
};

export default function MalKabulList() {
  usePageTitle('Mal Kabul');
  const navigate = useNavigate();
  const [belgeler, setBelgeler] = useState([]);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [arama, setArama] = useState('');
  const [durumSuzgec, setDurumSuzgec] = useState('');

  const getir = async () => {
    setYukleniyor(true);
    try {
      const r = await api.get('/teslimat');
      setBelgeler(r.data || []);
    } catch {
      toast.error('Mal kabul listesi yüklenemedi');
    }
    setYukleniyor(false);
  };
  useEffect(() => { getir(); }, []);

  const suzulmus = useMemo(() => {
    const q = trNorm(arama.trim());
    return belgeler.filter(b => {
      if (durumSuzgec && b.durum !== durumSuzgec) return false;
      if (!q) return true;
      return trNorm(`${b.belge_no || ''} ${b.supplier_name || ''}`).includes(q);
    });
  }, [belgeler, arama, durumSuzgec]);

  const sayim = useMemo(() => ({
    acik: belgeler.filter(b => b.durum === 'acik').length,
    kismi: belgeler.filter(b => b.durum === 'kismi').length,
    kapali: belgeler.filter(b => b.durum === 'kapali').length,
  }), [belgeler]);

  return (
    <div className="p-4 md:p-6 max-w-[1400px] mx-auto">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <h1 className="text-lg font-semibold text-[#1a1916] flex items-center gap-2">
          <PackageCheck size={18} className="text-[#4a5a3a]" /> Mal Kabul
        </h1>
        <Button onClick={() => navigate('/erp/mal-kabul/yeni')} size="sm"
          className="bg-[#4a5a3a] hover:bg-[#3d4a30] text-white" data-testid="mal-kabul-yeni">
          <Plus size={14} className="mr-1.5" /> Yeni Mal Kabul
        </Button>
      </div>

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#8a7e70]" />
          <Input value={arama} onChange={(e) => setArama(e.target.value)}
            placeholder="Belge no veya cari ara…"
            className="h-9 pl-8 text-sm border-[#d0c4b0]" data-testid="mal-kabul-ara" />
        </div>
        {[['', 'Tümü', belgeler.length], ['acik', 'Faturası bekleyen', sayim.acik],
          ['kismi', 'Kısmi', sayim.kismi], ['kapali', 'Faturalanan', sayim.kapali]]
          .map(([deger, etiket, n]) => (
            <button key={deger || 'tum'} onClick={() => setDurumSuzgec(deger)}
              className={`text-xs px-3 h-9 rounded-md border transition-colors
                ${durumSuzgec === deger
                  ? 'bg-[#4a5a3a] text-white border-[#4a5a3a]'
                  : 'bg-white text-[#6b5f4f] border-[#d0c4b0] hover:bg-[#f5f1e8]'}`}
              data-testid={`suzgec-${deger || 'tum'}`}>
              {etiket} <span className="opacity-70">({n})</span>
            </button>
          ))}
      </div>

      <Card className="border-[#d0c4b0]">
        <CardContent className="p-0">
          {yukleniyor ? (
            <p className="p-6 text-sm text-[#6b5f4f]">Yükleniyor…</p>
          ) : !suzulmus.length ? (
            <div className="p-10 text-center">
              <PackageCheck size={32} className="mx-auto text-[#d0c4b0] mb-2" />
              <p className="text-sm text-[#6b5f4f]">
                {belgeler.length ? 'Süzgece uyan kayıt yok.' : 'Henüz mal kabul kaydı yok.'}
              </p>
              {!belgeler.length && (
                <p className="text-xs text-[#8a7e70] mt-1">
                  İrsaliye, veresiye fişi ya da siparişten teslim alınan malı buradan girin.
                </p>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[820px]">
                <thead>
                  <tr className="bg-[#e8dfd0] text-[11px] uppercase text-[#1a1916]">
                    <th className="text-left p-2.5 font-bold">Teslim Tarihi</th>
                    <th className="text-left p-2.5 font-bold">Belge</th>
                    <th className="text-left p-2.5 font-bold">Cari</th>
                    <th className="text-right p-2.5 font-bold">Kalem</th>
                    <th className="text-right p-2.5 font-bold">Tutar</th>
                    <th className="text-left p-2.5 font-bold">Durum</th>
                  </tr>
                </thead>
                <tbody>
                  {suzulmus.map(b => {
                    const d = DURUM[b.durum] || DURUM.acik;
                    const yas = b.durum !== 'kapali' ? yasGun(b.teslim_tarihi) : null;
                    return (
                      <tr key={b.id}
                        onClick={() => navigate(`/erp/mal-kabul/${b.id}/duzenle`)}
                        className="border-b border-[#e8dfd0] cursor-pointer hover:bg-[#f5f0e8]">
                        <td className="p-2.5 text-[#3F3E39]">
                          {b.teslim_tarihi
                            ? new Date(b.teslim_tarihi).toLocaleDateString('tr-TR') : '—'}
                        </td>
                        <td className="p-2.5">
                          <span className="text-[#1a1916] font-medium">
                            {b.belge_no || '(no yok)'}
                          </span>
                          <span className="block text-[11px] text-[#8a7e70]">
                            {TUR[b.belge_turu] || b.belge_turu}
                            {b.purchase_order_id && ' · siparişten'}
                          </span>
                        </td>
                        <td className="p-2.5 text-[#1a1916]">{b.supplier_name}</td>
                        <td className="p-2.5 text-right font-mono">{b.kalem_sayisi}</td>
                        <td className="p-2.5 text-right font-mono">
                          {(b.tutar || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                        </td>
                        <td className="p-2.5">
                          <span className={`status-badge ${d.renk} inline-flex items-center gap-1`}>
                            <d.Ikon size={11} /> {d.etiket}
                          </span>
                          {yas != null && yas > 0 && (
                            <span className="block text-[10px] text-[#8a7e70] mt-0.5">
                              {yas} gündür bekliyor
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
